import React, { useState } from "react";
import api from "@/lib/api";
import { PageTitle } from "@/components/DashUI";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";
import { Rocket, Sparkles, Copy } from "lucide-react";

export default function BuildMyBusiness() {
  const settings = useSettings();
  const [f, setF] = useState({ business_name: "", owner: "", city: "", state: "", target_geography: "",
    provider_categories: "", consumer_niche: "", phone: "", email: "", domain: "", brand_preferences: "", revenue_goals: "" });
  const [out, setOut] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const run = async () => {
    setLoading(true); setOut("");
    try { const { data } = await api.post("/agent/ai/business-wizard", f); setOut(data.output); toast.success("Business plan generated"); }
    catch { toast.error("Generation failed"); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <PageTitle title="Build My Business" subtitle="Business-in-a-Box launch wizard" />
      <Card className="mb-6 flex items-center gap-4 bg-navy p-6 text-white">
        <Rocket className="h-8 w-8 text-clinical" />
        <div>
          <p className="font-heading font-semibold">The {settings.membership_price} membership teaches the business. The {settings.accelerator_price} Accelerator provides the machine.</p>
          <p className="mt-1 text-sm text-slate-300">Answer the questions below and let AI generate your launch package. Legal templates require independent legal review.</p>
        </div>
      </Card>
      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            {[["business_name", "Business name"], ["owner", "Owner"], ["city", "City"], ["state", "State"],
              ["target_geography", "Target geography"], ["consumer_niche", "Consumer niche"], ["phone", "Phone"],
              ["email", "Email"], ["domain", "Domain"], ["revenue_goals", "Revenue goals"]].map(([k, label]) => (
              <div key={k}><Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</Label>
                <Input data-testid={`wiz-${k}`} className="mt-1" value={f[k]} onChange={set(k)} /></div>
            ))}
          </div>
          <div className="mt-4 grid gap-4">
            <div><Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Provider categories</Label>
              <Input data-testid="wiz-providers" className="mt-1" value={f.provider_categories} onChange={set("provider_categories")} placeholder="e.g. Home care, assisted living" /></div>
            <div><Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Brand preferences</Label>
              <Textarea data-testid="wiz-brand" className="mt-1" value={f.brand_preferences} onChange={set("brand_preferences")} /></div>
          </div>
          <Button data-testid="wiz-generate-btn" onClick={run} disabled={loading} className="mt-4 w-full bg-clinical hover:bg-clinical/90">
            <Sparkles className="mr-2 h-4 w-4" /> {loading ? "Generating launch package…" : "Generate my business plan"}
          </Button>
        </Card>
        <Card className="flex flex-col p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-semibold text-navy">Your launch package</h3>
            {out && <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(out); toast.success("Copied"); }}><Copy className="mr-1.5 h-4 w-4" /> Copy</Button>}
          </div>
          <div className="mt-3 flex-1 overflow-y-auto whitespace-pre-wrap rounded-lg bg-slate-50 p-4 text-sm text-slate-700" data-testid="wiz-output">
            {out || <span className="text-slate-400">Your AI-generated business launch package will appear here.</span>}
          </div>
        </Card>
      </div>
    </div>
  );
}
